import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Sparkles, Pill, HeartPulse, Utensils, ClipboardList } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="fade-up">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-12 sm:pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold mb-6"
              style={{ backgroundColor: "#E8F0EC", color: "#23473C" }}
            >
              <Sparkles size={14} /> AI-powered wellness intelligence
            </div>
            <h1
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
              style={{ color: "#1C2826" }}
            >
              Understand every pill,{" "}
              <span style={{ color: "#2D5A4C" }}>supplement,</span> and sip in your routine.
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-2xl" style={{ color: "#5C6B66" }}>
              Harmonix reveals cross-interactions and possible symptoms across your
              medications, supplements, food, and alcohol — so you can have smarter
              conversations with your doctor.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-cta-primary"
                onClick={() => navigate(user ? "/dashboard" : "/register")}
                className="btn-brand rounded-full h-12 px-6 text-sm font-semibold"
              >
                {user ? "Open dashboard" : "Analyze my regimen"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
              <Button
                data-testid="hero-cta-secondary"
                variant="outline"
                onClick={() => navigate("/login")}
                className="rounded-full h-12 px-6 text-sm font-semibold border-[#E5DEC9]"
              >
                I already have an account
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 items-center text-sm" style={{ color: "#5C6B66" }}>
              <div className="flex items-center gap-2">
                <Shield size={16} style={{ color: "#2D5A4C" }} /> Private &amp; encrypted
              </div>
              <div className="flex items-center gap-2">
                <Sparkles size={16} style={{ color: "#D96B43" }} /> GPT 5.6 Terra clinical reasoning
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="card-warm p-2 overflow-hidden rounded-3xl">
              <img
                src="https://images.unsplash.com/photo-1545840716-c82e9eec6930?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Herbal wellness"
                className="w-full h-[380px] sm:h-[460px] object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#5C6B66" }}>
            What Harmonix checks
          </p>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-medium" style={{ color: "#1C2826" }}>
            A calm, complete picture — in one place.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Pill, title: "Drug ↔ Drug", desc: "Cross-check every prescription and OTC combination." , color:"#2D5A4C"},
            { icon: HeartPulse, title: "Symptoms & side effects", desc: "See likely symptoms clustered by likelihood.", color:"#D96B43" },
            { icon: Utensils, title: "Food & alcohol", desc: "Grapefruit, potassium, alcohol — all considered.", color:"#2D5A4C" },
            { icon: ClipboardList, title: "Severity clarity", desc: "Severe, moderate, mild — with plain-language guidance.", color:"#D96B43" },
          ].map((f, i) => (
            <div key={i} className="card-warm p-6">
              <div
                className="w-11 h-11 rounded-xl grid place-items-center mb-4"
                style={{ backgroundColor: "#F4EFE6" }}
              >
                <f.icon size={20} style={{ color: f.color }} />
              </div>
              <h3 className="font-serif-display text-xl font-semibold" style={{ color: "#1C2826" }}>
                {f.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "#5C6B66" }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* DISCLAIMER SPOTLIGHT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div
          className="rounded-3xl p-8 sm:p-12 grid md:grid-cols-5 gap-8 items-center"
          style={{ backgroundColor: "#F4EFE6", border: "1px solid #E5DEC9" }}
        >
          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#BF5832" }}>
              Important
            </p>
            <h3 className="mt-2 font-serif-display text-2xl sm:text-3xl font-medium" style={{ color: "#1C2826" }}>
              Harmonix informs — your doctor decides.
            </h3>
            <p className="mt-4 leading-relaxed" style={{ color: "#5C6B66" }}>
              This tool is designed to spark better conversations with clinicians. It is not
              a substitute for professional medical advice, diagnosis, or treatment. Always
              consult a qualified physician or pharmacist before changing any medication.
            </p>
          </div>
          <div className="md:col-span-2">
            <Link
              to={user ? "/dashboard" : "/register"}
              data-testid="disclaimer-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold btn-terracotta"
            >
              {user ? "Go to dashboard" : "Create free account"} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
